import { useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import {
  Check,
  ChevronDown,
  ChevronUp,
  Rocket,
  X,
  MapPin,
  Package,
  Wrench,
  CalendarClock,
  ClipboardCheck,
  LayoutDashboard,
  ArrowRight,
  type LucideIcon,
} from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { useTranslation } from "react-i18next";
import { useOnboarding } from "./OnboardingProvider";

const iconMap: Record<string, LucideIcon> = {
  "create-location": MapPin,
  "add-asset": Package,
  "create-work-order": Wrench,
  "setup-pm-schedule": CalendarClock,
  "run-inspection": ClipboardCheck,
  "view-dashboard": LayoutDashboard,
};

export function OnboardingChecklist() {
  const { t } = useTranslation("common");
  const {
    checklistItems,
    checklistDismissed,
    completeChecklistItem,
    dismissChecklist,
    checklistProgress,
    startModuleTour,
  } = useOnboarding();

  const [expanded, setExpanded] = useState(false);

  if (checklistDismissed) return null;

  const completedCount = checklistItems.filter((i) => i.completed).length;
  const totalCount = checklistItems.length;
  const allDone = completedCount === totalCount;

  return (
    <div className="fixed bottom-4 right-4 z-40">
      <AnimatePresence mode="wait">
        {!expanded ? (
          <motion.div
            key="collapsed"
            initial={{ opacity: 0, scale: 0.9, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 10 }}
            transition={{ duration: 0.2 }}
          >
            <Button
              onClick={() => setExpanded(true)}
              className="gap-2 rounded-full px-5 py-2.5 shadow-lg hover:shadow-xl transition-shadow"
              size="default"
            >
              <ProgressCircle progress={checklistProgress} size={20} />
              <span className="text-sm font-medium">
                {t("onboarding.checklist.title", "Getting Started")}
              </span>
              <ChevronUp className="size-4" />
            </Button>
          </motion.div>
        ) : (
          <motion.div
            key="expanded"
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
            transition={{ duration: 0.2 }}
            className="w-80 rounded-xl border bg-white shadow-xl dark:bg-slate-900 dark:border-slate-700"
          >
            {/* Header */}
            <div className="flex items-center justify-between border-b px-4 py-3 dark:border-slate-700">
              <div className="flex items-center gap-2">
                <Rocket className="size-4 text-primary" />
                <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                  {t("onboarding.checklist.title", "Getting Started")}
                </span>
              </div>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => setExpanded(false)}
                  className="flex size-7 items-center justify-center rounded-md text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800 dark:hover:text-slate-300"
                  aria-label="Collapse checklist"
                >
                  <ChevronDown className="size-3.5" />
                </button>
                <button
                  type="button"
                  onClick={dismissChecklist}
                  className="flex size-7 items-center justify-center rounded-md text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800 dark:hover:text-slate-300"
                  aria-label="Dismiss checklist"
                >
                  <X className="size-3.5" />
                </button>
              </div>
            </div>

            {/* Progress bar */}
            <div className="px-4 pt-3">
              <div className="mb-1 flex items-center justify-between">
                <span className="text-xs text-slate-500 dark:text-slate-400">
                  {t("onboarding.checklist.progress", "{{done}} of {{total}} completed", {
                    done: completedCount,
                    total: totalCount,
                  })}
                </span>
                <span className="text-xs font-medium text-primary">
                  {Math.round(checklistProgress * 100)}%
                </span>
              </div>
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
                <motion.div
                  className="h-full rounded-full bg-primary"
                  initial={{ width: 0 }}
                  animate={{ width: `${checklistProgress * 100}%` }}
                  transition={{ duration: 0.4, ease: "easeOut" }}
                />
              </div>
            </div>

            {/* Checklist items */}
            <div className="p-2">
              {checklistItems.map((item) => {
                const Icon = iconMap[item.id];
                const label = t(item.labelKey ?? "", item.label ?? "");
                const description = item.descriptionKey
                  ? t(item.descriptionKey, "")
                  : "";

                return (
                  <a
                    key={item.id}
                    href={item.href}
                    onClick={(e) => {
                      if (!item.completed && item.tourId) {
                        e.preventDefault();
                        completeChecklistItem(item.id);
                        startModuleTour(item.tourId);
                      } else if (!item.completed) {
                        completeChecklistItem(item.id);
                      }
                    }}
                    className={`group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-all duration-150 ${
                      item.completed
                        ? "opacity-60 hover:opacity-80 hover:bg-slate-50 dark:hover:bg-slate-800/50"
                        : "hover:bg-primary/5 hover:pl-4 dark:hover:bg-primary/10 cursor-pointer"
                    }`}
                  >
                    {/* Status icon */}
                    {item.completed ? (
                      <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-primary text-white">
                        <Check className="size-3.5" strokeWidth={3} />
                      </span>
                    ) : Icon ? (
                      <span className="flex size-6 shrink-0 items-center justify-center rounded-full border-2 border-slate-200 text-slate-400 transition-colors group-hover:border-primary/50 group-hover:text-primary dark:border-slate-600 dark:group-hover:border-primary/50">
                        <Icon className="size-3.5" />
                      </span>
                    ) : (
                      <span className="flex size-6 shrink-0 items-center justify-center rounded-full border-2 border-slate-200 transition-colors group-hover:border-primary/50 dark:border-slate-600" />
                    )}

                    {/* Text */}
                    <div className="flex flex-1 flex-col min-w-0">
                      <span
                        className={
                          item.completed
                            ? "text-slate-400 line-through dark:text-slate-500"
                            : "text-slate-700 font-medium transition-colors group-hover:text-slate-900 dark:text-slate-200 dark:group-hover:text-white"
                        }
                      >
                        {label}
                      </span>
                      {description && (
                        <span className="text-xs text-slate-400 dark:text-slate-500">
                          {description}
                        </span>
                      )}
                    </div>

                    {/* Arrow indicator on hover for incomplete items */}
                    {!item.completed && (
                      <ArrowRight className="size-3.5 shrink-0 text-slate-300 opacity-0 transition-all duration-150 group-hover:opacity-100 group-hover:text-primary dark:text-slate-600" />
                    )}
                  </a>
                );
              })}
            </div>

            {/* Dismiss footer */}
            {allDone && (
              <div className="border-t px-4 py-3 dark:border-slate-700">
                <p className="mb-2 text-center text-sm text-slate-600 dark:text-slate-300">
                  {t("onboarding.checklist.allDone", "You're all set!")}
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={dismissChecklist}
                >
                  {t("onboarding.checklist.dismiss", "Dismiss")}
                </Button>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/** Small circular progress indicator for the collapsed button */
function ProgressCircle({
  progress,
  size = 20,
}: {
  progress: number;
  size?: number;
}) {
  const strokeWidth = 2.5;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - progress * circumference;

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        opacity={0.2}
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        strokeLinecap="round"
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
        style={{ transition: "stroke-dashoffset 0.4s ease" }}
      />
    </svg>
  );
}
